import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CompanyContext';
import { toast } from 'sonner';
import {
  TrendUp,
  TrendDown,
  Wallet,
  Bank,
  ChartLineUp,
  ChartPie,
  Warning,
  Info,
  Calendar,
  CaretRight,
  ArrowUp,
  ArrowDown
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';

import { useFinanceDrill } from '../../components/reports/useFinanceDrill';
import { DataNote } from '../../components/reports/DataNote';
import { TrendBarChart } from '../../components/reports/FinanceCharts';

const API = process.env.REACT_APP_BACKEND_URL;
const compactAxis = (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`);

export default function AnalyticsDashboardPage() {
  const { token } = useAuth();
  const { formatCurrency } = useCurrency();
  const { openDrill, openParty, dialogs } = useFinanceDrill();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 12)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      
      const res = await fetch(`${API}/api/analytics/dashboard?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setDashboardData(await res.json());
      }
    } catch (err) {
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [token, dateRange]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const formatPercent = (value) => {
    return `${value >= 0 ? '+' : ''}${value?.toFixed(1)}%`;
  };

  const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const summary = dashboardData?.summary || {};
  const monthlyTrend = dashboardData?.monthly_trend || [];
  const profitMargins = dashboardData?.profit_margins || [];
  const cashFlowTrend = dashboardData?.cash_flow_trend || [];
  const expenseBreakdown = dashboardData?.expense_breakdown || [];
  const receivablesAging = dashboardData?.receivables_aging || [];
  const payablesAging = dashboardData?.payables_aging || [];

  const statCards = [
    { 
      label: 'Total Revenue', 
      value: formatCurrency(summary.total_revenue),
      icon: TrendUp,
      color: 'bg-emerald-500/10 text-emerald-600',
      drill: { category: 'income', title: 'Total Revenue — income entries before VAT' },
      hint: 'Income accounts, before VAT',
    },
    { 
      label: 'Total Expenses', 
      value: formatCurrency(summary.total_expenses),
      icon: TrendDown,
      color: 'bg-red-500/10 text-red-600',
      drill: { category: 'expense', title: 'Total Expenses — expense entries' },
      hint: 'Expense accounts, before VAT',
    },
    { 
      label: 'Net Profit', 
      value: formatCurrency(summary.net_profit),
      icon: Wallet,
      color: summary.net_profit >= 0 ? 'bg-blue-500/10 text-blue-600' : 'bg-red-500/10 text-red-600',
      drill: { category: 'pnl', title: 'Net Profit — revenue less expenses' },
      hint: 'Revenue − Expenses',
    },
    { 
      label: 'Profit Margin', 
      value: `${summary.profit_margin?.toFixed(1)}%`,
      icon: ChartPie,
      color: 'bg-purple-500/10 text-purple-600',
      drill: { category: 'pnl', title: 'Profit Margin — Net Profit ÷ Revenue × 100', explain: `Profit Margin = Net Profit ÷ Revenue × 100 = ${formatCurrency(summary.net_profit)} ÷ ${formatCurrency(summary.total_revenue)} × 100. The entries below make up both figures (income adds, expenses subtract).` },
      hint: 'Net Profit ÷ Revenue',
    },
  ];

  return (
    <div className="space-y-8" data-testid="analytics-dashboard-page">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">
            Analytics Dashboard
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-1">
            Comprehensive financial insights and performance metrics — click any card or chart for the entries behind it
          </p>
        </div>
        
        {/* Date Range Filter */}
        <div className="flex items-center gap-2">
          <Input 
            type="date" 
            value={dateRange.start}
            onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
            className="w-[140px]"
          />
          <span className="text-muted-foreground">to</span>
          <Input 
            type="date" 
            value={dateRange.end}
            onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
            className="w-[140px]"
          />
          <Button onClick={fetchDashboard} variant="outline" size="sm">
            Apply
          </Button>
        </div>
      </motion.div>

      <DataNote />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            onClick={() => openDrill({ ...stat.drill, startDate: dateRange.start, endDate: dateRange.end })}
            className="card-tactile rounded-lg p-4 sm:p-6 cursor-pointer hover:ring-2 hover:ring-primary/40 transition-shadow"
            data-testid={`analytics-card-${stat.drill.category}-${index}`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${stat.color}`}>
                <stat.icon size={20} weight="duotone" />
              </div>
              <div>
                <span className="text-xs tracking-wider uppercase font-semibold text-muted-foreground">
                  {stat.label}
                </span>
                <p className="text-xl sm:text-2xl font-heading font-semibold mt-0.5">
                  {stat.value}
                </p>
                <p className="text-[11px] text-muted-foreground">{stat.hint} · click for details</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* AR / AP Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          onClick={() => openParty('customer')}
          className="card-tactile rounded-lg p-6 cursor-pointer hover:ring-2 hover:ring-primary/40 transition-shadow"
          data-testid="analytics-card-receivables"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                Accounts Receivable
              </p>
              <p className="text-2xl font-heading font-semibold mt-1 text-blue-600">
                {formatCurrency(summary.total_receivables)}
              </p>
              <p className="text-[11px] text-muted-foreground">Unpaid invoice balances (Statement of Accounts) · click for details</p>
            </div>
            <Bank size={32} weight="duotone" className="text-blue-500 opacity-50" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          onClick={() => openParty('supplier')}
          className="card-tactile rounded-lg p-6 cursor-pointer hover:ring-2 hover:ring-primary/40 transition-shadow"
          data-testid="analytics-card-payables"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                Accounts Payable
              </p>
              <p className="text-2xl font-heading font-semibold mt-1 text-amber-600">
                {formatCurrency(summary.total_payables)}
              </p>
              <p className="text-[11px] text-muted-foreground">Unpaid approved supplier bills · click for details</p>
            </div>
            <Wallet size={32} weight="duotone" className="text-amber-500 opacity-50" />
          </div>
        </motion.div>
      </div>

      {/* Revenue vs Expense Trend */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="card-tactile rounded-lg p-6"
      >
        <h3 className="font-heading text-lg font-medium">Revenue vs Expenses Trend</h3>
        <p className="text-xs text-muted-foreground mb-4">Monthly, from posted journal entries (before VAT) — same as Profit &amp; Loss</p>
        <div className="h-[300px]">
          {monthlyTrend.length > 0 ? (
            <TrendBarChart data={monthlyTrend} height={300} series={[{ key: 'revenue', label: 'Revenue', color: '#10b981' }, { key: 'expense', label: 'Expense', color: '#f59e0b' }]} />
          ) : (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <ChartLineUp size={48} weight="duotone" className="mx-auto mb-2 opacity-50" />
                <p>No trend data available</p>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Profit Margin & Cash Flow */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profit Margin Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card-tactile rounded-lg p-6"
        >
          <h3 className="font-heading text-lg font-medium mb-4">Profit Margin Trend</h3>
          <div className="h-[250px]">
            {profitMargins.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={profitMargins}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="month" className="text-xs" />
                  <YAxis className="text-xs" tickFormatter={(v) => `${v}%`} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => [`${value}%`, 'Margin']}
                  />
                  <Line type="monotone" dataKey="margin" name="Profit Margin" stroke="#8b5cf6" strokeWidth={2} dot={{ fill: '#8b5cf6' }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <p>No margin data available</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Cash Flow Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="card-tactile rounded-lg p-6"
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-heading text-lg font-medium">Cash Flow Trend</h3>
              <p className="text-xs text-muted-foreground">Money in vs out of Cash &amp; Bank accounts · In {formatCurrency(summary.cash_in)} / Out {formatCurrency(summary.cash_out)}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => openDrill({ category: 'cash', title: 'Cash & Bank movements', startDate: dateRange.start, endDate: dateRange.end })} data-testid="analytics-cash-details-btn">Details</Button>
          </div>
          <div className="h-[250px]">
            {cashFlowTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cashFlowTrend}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="month" className="text-xs" />
                  <YAxis className="text-xs" tickFormatter={compactAxis} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value, name) => [formatCurrency(value), name]}
                  />
                  <Legend />
                  <Bar dataKey="cash_in" name="Money in" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="cash_out" name="Money out" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="cash_flow" name="Net" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <p>No cash flow data available</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Expense Breakdown & Aging */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Expense Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card-tactile rounded-lg p-6"
        >
          <h3 className="font-heading text-lg font-medium mb-4">Expense Breakdown</h3>
          <div className="h-[250px] [&_*:focus]:outline-none">
            {expenseBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={expenseBreakdown.slice(0, 6)}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="amount"
                    nameKey="category"
                    className="cursor-pointer"
                    onClick={(d) => { const e = d?.payload || d; openDrill({ category: 'expense', accountId: e.account_id, title: e.category, startDate: dateRange.start, endDate: dateRange.end }); }}
                  >
                    {expenseBreakdown.slice(0, 6).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => [formatCurrency(value), '']}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <p>No expense data</p>
              </div>
            )}
          </div>
          {expenseBreakdown.length > 0 && (
            <div className="mt-4 space-y-2">
              {expenseBreakdown.slice(0, 5).map((exp, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                    <button type="button" className="truncate max-w-[150px] hover:underline text-left" onClick={() => openDrill({ category: 'expense', accountId: exp.account_id, title: exp.category, startDate: dateRange.start, endDate: dateRange.end })}>{exp.category}</button>
                  </div>
                  <span className="font-mono">{formatCurrency(exp.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Receivables Aging */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="card-tactile rounded-lg p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-medium">Receivables Aging</h3>
            <Button variant="outline" size="sm" onClick={() => openParty('customer')} data-testid="analytics-ar-aging-btn">Details</Button>
          </div>
          <div className="space-y-3">
            {receivablesAging.map((bucket, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <span className="text-sm">{bucket.bucket}</span>
                <div className="flex items-center gap-2">
                  <div className="w-16 h-2 bg-muted rounded-full overflow-hidden shrink-0">
                    <div 
                      className={`h-full ${bucket.bucket === '90+ days' ? 'bg-red-500' : bucket.bucket.includes('60') ? 'bg-amber-500' : 'bg-blue-500'}`}
                      style={{ width: `${Math.min((bucket.amount / (summary.total_receivables || 1)) * 100, 100)}%` }}
                    />
                  </div>
                  <span className="font-mono text-xs min-w-[7.5rem] text-right whitespace-nowrap">{formatCurrency(bucket.amount)}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Payables Aging */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="card-tactile rounded-lg p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-medium">Payables Aging</h3>
            <Button variant="outline" size="sm" onClick={() => openParty('supplier')} data-testid="analytics-ap-aging-btn">Details</Button>
          </div>
          <div className="space-y-3">
            {payablesAging.map((bucket, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <span className="text-sm">{bucket.bucket}</span>
                <div className="flex items-center gap-2">
                  <div className="w-16 h-2 bg-muted rounded-full overflow-hidden shrink-0">
                    <div 
                      className={`h-full ${bucket.bucket === '90+ days' ? 'bg-red-500' : bucket.bucket.includes('60') ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      style={{ width: `${Math.min((bucket.amount / (summary.total_payables || 1)) * 100, 100)}%` }}
                    />
                  </div>
                  <span className="font-mono text-xs min-w-[7.5rem] text-right whitespace-nowrap">{formatCurrency(bucket.amount)}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
      {dialogs}
    </div>
  );
}
